import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { styled, keyframes, alpha } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../../assets/Logo.png';
import axios from 'axios';

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const floatingBubbles = keyframes`
  0% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-20px) scale(1.1); }
  100% { transform: translateY(0) scale(1); }
`;

const Container = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  minWidth: '100vw',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  background: 'linear-gradient(-45deg, #60a5fa, #34d399, #3b82f6, #f472b6)',
  backgroundSize: '400% 400%',
  animation: `${gradientAnimation} 15s ease infinite`,
  position: 'relative',
  overflow: 'hidden',

  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    animation: `${floatingBubbles} 6s infinite ease-in-out`,
    [theme.breakpoints.down('sm')]: {
      width: '300px',
      height: '300px',
    }
  },

  '&::before': {
    top: '-100px',
    left: '-100px',
    animationDelay: '0s',
  },

  '&::after': {
    bottom: '-100px',
    right: '-100px',
    animationDelay: '-3s',
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 450,
  minWidth: 320,
  borderRadius: theme.spacing(2),
  backdropFilter: 'blur(10px)',
  backgroundColor: theme.palette.mode === 'dark' 
    ? alpha(theme.palette.background.paper, 0.8)
    : 'rgba(255, 255, 255, 0.9)',
  position: 'relative',
  zIndex: 1,
  transition: 'transform 0.3s ease-in-out',
  
  '& .MuiTypography-root': {
    color: theme.palette.text.primary,
  },
  
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
  },
  
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
  },
  
  '& .MuiFormHelperText-root': {
    color: theme.palette.text.secondary,
  },
  
  '& .MuiLink-root': {
    color: theme.palette.primary.main,
  },
  
  '& .MuiTypography-colorTextSecondary': {
    color: theme.palette.text.secondary,
  },
  
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  
  '&:hover': {
    transform: 'translateY(-5px)'
  },

  [theme.breakpoints.up('lg')]: { maxWidth: 500 },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '95%',
    margin: theme.spacing(2),
  }
}));

const ContentWrapper = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(4),
  [theme.breakpoints.up('lg')]: { padding: theme.spacing(5) },
  [theme.breakpoints.down('sm')]: { padding: theme.spacing(3) }
}));

const Logo = styled('img')(({ theme }) => ({
  width: 80,
  height: 80,
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: { width: 60, height: 60 }
}));

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isValidEmail = (val) => /^\S+@\S+\.\S+$/.test(val);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError('');

    if (!email) {
      setFieldError('Email is required');
      toast.error('Email is required');
      return;
    }
    if (!isValidEmail(email)) {
      setFieldError('Please enter a valid email');
      toast.error('Please enter a valid email');
      return;
    }

    setIsLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL;

      // FIX: use the auth-prefixed route to match your backend
      const { data } = await axios.post(`${apiUrl}/auth/forgot-password`, { email });

      // backend typical success: { success:true, message: "..." }
      const message = data?.message || 'Reset link sent successfully!';
      toast.success(message, { icon: '✉️', theme: 'colored' });
    } catch (error) {
      // Prefer backend message; fall back to generic
      const apiErrors = error?.response?.data?.errors;
      const apiMessage = error?.response?.data?.message;

      if (apiErrors?.email?.[0]) {
        setFieldError(apiErrors.email[0]);
        toast.error(apiErrors.email[0]);
      } else {
        toast.error(apiMessage || 'Failed to send reset link');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <ToastContainer />
      <StyledCard>
        <ContentWrapper>
          <Stack spacing={3} alignItems="center">
            <Logo src={logo} alt="Logo" />
            <Typography variant="h4" component="h1">
              Forgot Password?
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center">
              Enter your email address and we&apos;ll send you instructions to reset your password.
            </Typography>

            <form onSubmit={handleSubmit} style={{ width: '100%' }} noValidate>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!fieldError}
                  helperText={fieldError}
                  required
                  disabled={isLoading}
                  sx={{
                    '& .MuiInputBase-root': { height: { xs: 45, sm: 56 } }
                  }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  type="submit"
                  disabled={isLoading}
                  sx={{
                    height: { xs: 45, sm: 56 },
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }}
                >
                  {isLoading ? 'Sending...' : 'Send Reset Instructions'}
                </Button>

                <Link to="/" style={{ textDecoration: 'none' }}>
                  <Typography
                    color="primary"
                    align="center"
                    sx={{ '&:hover': { textDecoration: 'underline' } }}
                  >
                    Back to Login
                  </Typography>
                </Link>
              </Stack>
            </form>
          </Stack>
        </ContentWrapper>
      </StyledCard>
    </Container>
  );
};

export default ForgotPassword;
